/**
 * ToastHandler Component
 *
 * This component handles displaying toast notifications based on URL parameters, while being able to navigate at the same time.
 * It uses a data-driven approach with a configurable array of toast definitions,
 * making it easy to add new types of toast messages.
 *
 * ## How to Use:
 *
 * 1. Trigger toasts by adding URL parameters when navigating:
 *    - For info messages: `?infoMessage=Your session will expire soon`
 *    - For error messages: `?errorMessage=There is no English version of this page.`
 *    - For success messages: `?successMessage=Operation completed successfully`
 *
 * 2. Example usage with navigateWithParams to show an error message
 *    const params = new Map<string, string>();
 *    params.set('errorMessage', 'There is no English version of this page.');
 *    navigateWithParams(url, true, params);
 *    ```
 */

import React, { useEffect } from "react";
import { useLocation } from "@reach/router";
import { toast, Toast } from "@hrk/huw-module-library";

const toastConfigs = [
  {
    paramName: "successMessage",
    type: "success",
  },
  {
    paramName: "errorMessage",
    type: "error",
  },
  {
    paramName: "infoMessage",
    type: "info",
  },
];

export const ToastHandler: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(location.search);

    const handleToasts = async () => {
      const cleanParams = new URLSearchParams(location.search);
      let paramsChanged = false;

      for (const config of toastConfigs) {
        const paramValue = params.get(config.paramName);

        if (paramValue) {
          switch (config.type) {
            case "success":
              toast.success(paramValue);
              break;
            case "error":
              toast.error(paramValue);
              break;
            case "info":
              toast(paramValue);
              break;
          }

          cleanParams.delete(config.paramName);
          paramsChanged = true;
        }
      }

      if (paramsChanged) {
        const newUrl = `${location.pathname}${cleanParams.toString() ? `?${cleanParams.toString()}` : ""}`;
        window.history.replaceState({}, "", newUrl);
      }
    };

    handleToasts();
  }, [location]);

  return (
    <>
      <Toast />
      {children}
    </>
  );
};
